import isNil from 'lodash/isNil'
import { z } from 'zod'
// TODO(WF-856): remove this dependency!
import { FormElementTypeSchema } from '@omnea-app/packages/universal/form/FormElementTypeSchema'
import { ref, refOn } from '@omnea-app/packages/universal/references/refKinds'
import { assertNever } from '@omnea-app/packages/universal/type-guards/assertNever'
import { isDefined } from '@omnea-app/packages/universal/type-guards/isDefined'
import { propertyIsDefined } from '@omnea-app/packages/universal/type-guards/propertyIsDefined'
import { captureAndReturn } from '@omnea-app/packages/universal/zod-utils/captureAndReturn'

// 1. OPERATORS

export const ALL_OPERATORS = [
  'IS_EMPTY',
  'IS_NOT_EMPTY',
  'EQUAL',
  'NOT_EQUAL',
  'CONTAINS',
  'NOT_CONTAINS',
  'GREATER_THAN',
  'LESS_THAN',
  'GREATER_THAN_OR_EQUAL_TO',
  'LESS_THAN_OR_EQUAL_TO',
  'IS_ONE_OF',
  'IS_NOT_ONE_OF',
  'CONTAINS_ALL',
  'CONTAINS_ANY',
  'DOES_NOT_CONTAIN_ANY',
] as const
export const ConditionOperatorSchema = z.enum(ALL_OPERATORS)
export type ConditionOperator = z.infer<typeof ConditionOperatorSchema>
export const isValidOperator = (value: unknown): value is ConditionOperator => {
  return ConditionOperatorSchema.safeParse(value).success
}

// 2. OPERANDS

export const FormQuestionOperandSchema = z.object({
  type: z.literal('VARIABLE'),
  value: z.string(),
  questionType: FormElementTypeSchema.optional(), // TODO(WF-856): remove this
  variant: z.string().optional(),
})
export type FormQuestionOperand = z.infer<typeof FormQuestionOperandSchema>

export const ConditionGroupOperandSchema = z.object({
  type: z.literal('VARIABLE_CONDITION_GROUP'),
  value: ref('conditionGroup'),
})
export type ConditionGroupOperand = z.infer<typeof ConditionGroupOperandSchema>
export const isConditionGroupOperand = (value: LeftOperand | null) => {
  return value != null && value.type === 'VARIABLE_CONDITION_GROUP'
}

export const RequestPropertyOperandSchema = z.object({
  type: z.literal('REQUEST_VARIABLE'),
  value: z.enum([
    'derivedFields.risk.aggregate',
    'derivedFields.lineItemsTotal.amount',
    'derivedFields.lineItemsVsAgreementDelta.amount',
    'budget.isRequestOverBudget',
    'supplier.name',
    'supplier.legalName',
    'supplier.state',
    'supplier.website',
    'supplier.remoteId',
    'supplier.profile.state',
    'purchaseOrder.status',
    'purchaseOrder.remoteStatus',
    'inherentRisk.tier',
    'inherentRisk.weightedScore',
  ]),
  autoFlaggedScoreCategoryId: refOn(z.uuid(), 'autoFlaggedScoreCategory').optional(),
})
export type RequestPropertyOperand = z.infer<typeof RequestPropertyOperandSchema>

export const ConstantOperandSchema = z.object({
  type: z.literal('CONSTANT'),
  value: z.string(),
})
export type ConstantOperand = z.infer<typeof ConstantOperandSchema>

// Aka no RHS
export const UnaryOperandSchema = z.object({
  type: z.literal('UNARY'),
})
export type UnaryOperand = z.infer<typeof UnaryOperandSchema>

const CoreDataVariableOperandSchema = z.object({
  type: z.literal('DYNAMIC_OPTION_VARIABLE'),
  value: z.string(),
  // The operand keeps its own copy rather than reading the question's, so it outlives a change to
  // that question's dataSource and has to be recorded on its own account.
  source: ref('coreDataSet'),
  questionType: z.literal('dynamic-option'),
})
export type CoreDataVariableOperand = z.infer<typeof CoreDataVariableOperandSchema>

const CoreDataValueOperandSchema = z.object({
  type: z.literal('CORE_DATA_VALUE').optional(), // Optional because historically this property was not present
  // `source` says which table the option lives in, so an id alone does not identify it.
  id: ref('coreDataOption', { scopeFrom: 'source' }),
  source: ref('coreDataSet'),
})
export type CoreDataValueOperand = z.infer<typeof CoreDataValueOperandSchema>

const MultiValueOperandSchema = z.object({
  type: z.literal('MULTI_VALUE'),
  values: z.array(z.discriminatedUnion('type', [FormQuestionOperandSchema, ConstantOperandSchema, CoreDataValueOperandSchema])),
})
export type MultiValueOperand = z.infer<typeof MultiValueOperandSchema>

const LeftOperandSchema = z
  .discriminatedUnion('type', [
    FormQuestionOperandSchema,
    ConditionGroupOperandSchema,
    RequestPropertyOperandSchema,
    CoreDataVariableOperandSchema,
  ])
  .catch(captureAndReturn('LeftOperandSchema'))
export type LeftOperand = z.infer<typeof LeftOperandSchema>
export const isValidLeftOperand = (value: unknown): value is LeftOperand => {
  return LeftOperandSchema.removeCatch().safeParse(value).success
}

export const RightOperandSchema = z
  .discriminatedUnion('type', [
    UnaryOperandSchema,
    FormQuestionOperandSchema,
    // ConditionGroupOperandSchema,
    // RequestPropertyOperandSchema,
    MultiValueOperandSchema,
    ConstantOperandSchema,
    CoreDataValueOperandSchema,
  ])
  .catch(captureAndReturn('RightOperandSchema'))
export type RightOperand = z.infer<typeof RightOperandSchema>
// HACK: This allows us to temporarily diverge what is supported in building config (FE) vs evaluating (BE)
export type SupportedRightOperand = Exclude<RightOperand, MultiValueOperand>

export const isValidRightOperand = (value: unknown): value is RightOperand => {
  return RightOperandSchema.removeCatch().safeParse(value).success
}

export const isRightOperand = (input: LeftOperand | RightOperand | null): input is RightOperand => {
  return (
    isDefined(input) &&
    (isNil(input.type) || ['MULTI_VALUE', 'VARIABLE', 'CORE_DATA_VALUE', 'UNARY', 'CONSTANT'].includes(input.type))
  )
}

export type OperandType = (LeftOperand | RightOperand)['type']

// 3. COMPARISION OPERATIONS

/**
 * A comparison whose outcome is already known. The server substitutes it for a comparison the client
 * cannot evaluate before a config leaves the server, so it exists only in served configs. It is a type
 * and not a zod member on purpose: every write boundary parses `ComparisonSchema`, and keeping ALWAYS
 * out of that union is what stops a client from storing one.
 */
export type AlwaysComparison = { type: 'ALWAYS'; key: number; value: boolean }

export const alwaysTrue = (key: number): AlwaysComparison => ({ type: 'ALWAYS', key, value: true })
export const alwaysFalse = (key: number): AlwaysComparison => ({ type: 'ALWAYS', key, value: false })

/**
 * The comparison schemas for one subject: a SINGLE leaf carrying that subject's left operand, and the
 * AND/OR group over it. Only the left operand varies between subjects, so declaring the rest once is
 * what keeps a field added to a comparison reaching all of them.
 *
 * `annotate` exists because the request's tree is published as `Shared:Comparison` and a
 * subject-specific one must not be. It applies at build time rather than after, since `.openapi()`
 * clones and a clone would no longer be the object the union holds.
 */
export const comparisonSchemasFor = <TLeft extends z.ZodType>(
  leftOperand: TLeft,
  annotate: { items?: <S extends z.ZodType>(s: S) => S; tree?: <S extends z.ZodType>(s: S) => S } = {}
) => {
  const single = z.object({
    primaryField: leftOperand.nullable(),
    operator: ConditionOperatorSchema.nullable(),
    secondaryField: RightOperandSchema.nullable(),
    key: z.number(),
    type: z.literal('SINGLE'),
    name: z.string().optional(),
  })
  type Single = z.infer<typeof single>

  const lazyItems = z.lazy(() => z.array(tree))
  const group = z.object({
    key: z.number(),
    type: z.enum(['AND', 'OR']),
    items: annotate.items ? annotate.items(lazyItems) : lazyItems,
  })

  // `tree` appears inside its own definition through the lazy items above, and a type that references
  // itself is one the compiler cannot infer — so it is stated here rather than derived.
  const built = z.discriminatedUnion('type', [single, group])
  const tree = (annotate.tree ? annotate.tree(built) : built) as z.ZodType<ComparisonTree<Single>>

  return { single, group, tree }
}

const requestComparison = comparisonSchemasFor(LeftOperandSchema, {
  // Zod-to-OpenAPI cannot express `.lazy`, so the recursion is declared as a plain array here and the
  // real type is substituted during generation.
  items: (items) => items.openapi('Shared:MultipleComparisonItems', { type: 'array', items: { type: 'object' } }),
  tree: (tree) => tree.openapi('Shared:Comparison'),
})

export const SingleComparisonSchema = requestComparison.single
export type SingleComparison = z.infer<typeof SingleComparisonSchema>

export type MultipleComparison = {
  key: number
  type: 'AND' | 'OR'
  items: Comparison[]
}
export type Comparison = SingleComparison | MultipleComparison | AlwaysComparison

/**
 * The AND/OR tree, parameterised by its leaf. A subject whose operands differ from a request's — a
 * condition read against a person — reuses the shape without joining `Comparison`, which is
 * published as `Shared:Comparison` and so cannot take a leaf only that subject understands.
 */
export type ComparisonTree<TSingle> =
  | TSingle
  | AlwaysComparison
  | { key: number; type: 'AND' | 'OR'; items: ComparisonTree<TSingle>[] }

export const MultipleComparisonSchema = requestComparison.group

// The discriminant is checked before the other fields, so a malformed leaf cannot be mistaken for a group.
export const ComparisonSchema = requestComparison.tree

// 4. CONDITION CONFIG

export const ConditionConfigSchema = z.object({
  id: z.string(),
  name: z.string(),
  config: ComparisonSchema,
})

export type ConditionConfig = z.infer<typeof ConditionConfigSchema>

// A block's own conditionConfig never has an `id` — only a saved condition group does.
export const WorkflowConditionConfigSchema = ConditionConfigSchema.omit({ id: true })
export type WorkflowConditionConfig = z.infer<typeof WorkflowConditionConfigSchema>

// 5. UTILS
export const isConstantOperand = (a: LeftOperand | RightOperand | null): a is ConstantOperand => {
  return isDefined(a) && a.type === 'CONSTANT'
}

export const isUnaryOperand = (a: LeftOperand | RightOperand | null): a is UnaryOperand => {
  return isDefined(a) && a.type === 'UNARY'
}

export const isMultiValueOperand = (a: LeftOperand | RightOperand | null): a is MultiValueOperand => {
  return isDefined(a) && a.type === 'MULTI_VALUE'
}

export const isCoreDataValueOperand = (input: LeftOperand | RightOperand | null): input is CoreDataValueOperand => {
  return !!input && (typeof input.type === 'undefined' || input.type === 'CORE_DATA_VALUE') && propertyIsDefined(input, 'source')
}

// TODO: remove entirely with V1. Used to hide operators from the FE.
export const isUnsupportedOperand = (input: LeftOperand | RightOperand | null): input is never => {
  return false
}

export const isCoreDataVariableOperand = (input: LeftOperand | RightOperand | null): input is CoreDataVariableOperand => {
  return propertyIsDefined(input, 'type') && input.type === 'DYNAMIC_OPTION_VARIABLE'
}

export type SingleRightOperand = Exclude<RightOperand, MultiValueOperand>

const UNARY_OPERAND_ID = '__UNARY__'

// Used for excluding possible values and equality checks
export const getIdForSingleRightOperand = (rhs: SingleRightOperand): string | null => {
  switch (rhs.type) {
    case 'CORE_DATA_VALUE':
    case undefined:
      return rhs.id
    case 'VARIABLE':
    case 'CONSTANT':
      // Note: the type of rhs.value allows an array of string but that is not supported here
      return Array.isArray(rhs.value) ? null : rhs.value
    case 'UNARY':
      return UNARY_OPERAND_ID
    default:
      assertNever(rhs)
      return null
  }
}

export type GlobalOperandTypeMeta = 'string' | 'number' | 'boolean' | 'stringArray' | 'date'
